import * as React from "react";
import {ErrorInfo, ReactNode, useContext} from "react";
import {Dialog} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import * as Sentry from "@sentry/react";
import {useTranslation, WithTranslation, withTranslation} from "react-i18next";
import {UserManagerContext} from "../auth/UserManagerContext";

type Props = {children?: ReactNode} & WithTranslation;
type State = {
  showDialog: boolean;
  error?: string;
};

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      showDialog: false,
    };
  }

  private promiseRejectionHandler = (event: PromiseRejectionEvent) => {
    console.log(`#promiseRejectionHandler`, event);
    const reason = event.reason.status
      ? `status=${event.reason.status},statusText=${event.reason.statusText}`
      : event.reason;
    this.setState({
      showDialog: true,
      error: `Uncaught promise rejection: ${reason}`,
    });
  };

  static getDerivedStateFromError(error: Error): State {
    return {
      showDialog: true,
      error: error.message,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
    this.setState({
      error: error.message,
    });
  }

  componentDidMount() {
    // Actually, Sentry.ErrorBoundary is supposed to register unhandledrejection,
    // but it's not working, so we do it ourselves.
    window.addEventListener("unhandledrejection", this.promiseRejectionHandler);
  }

  componentWillUnmount() {
    window.removeEventListener("unhandledrejection", this.promiseRejectionHandler);
  }

  render() {
    const showError = () => (
      <Dialog open={true}>
        <DialogTitle id="alert-dialog-title">
          {this.props.t("errorBoundaryDialog.title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {this.props.t("errorBoundaryDialog.text")}
            {this.state.error ? (
              <>
                <br />
                {this.state.error.toString()}
              </>
            ) : undefined}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => window.location.reload()} autoFocus={true}>
            {this.props.t("errorBoundaryDialog.reloadButton")}
          </Button>
          <Button onClick={() => (window.location.href = "/")}>
            {this.props.t("errorBoundaryDialog.homeButton")}
          </Button>
          <SignOutButton />
          <Button
            onClick={() => {
              this.setState({error: undefined});
            }}
          >
            {this.props.t("errorBoundaryDialog.closeButton")}
          </Button>
        </DialogActions>
      </Dialog>
    );

    return (
      <Sentry.ErrorBoundary showDialog={this.state.showDialog}>
        {this.props.children}
        {this.state.error ? showError() : undefined}
      </Sentry.ErrorBoundary>
    );
  }
}

export default withTranslation()(ErrorBoundary);

function SignOutButton() {
  const {t} = useTranslation();
  // Absent if the error occurred before the UserManager was created (e.g. /api/config failed)
  const userManager = useContext(UserManagerContext);
  if (!userManager) return null;
  return (
    <Button
      onClick={async () => {
        await userManager.signoutRedirect({
          post_logout_redirect_uri: `${window.location.href}`,
        });
      }}
    >
      {t("errorBoundaryDialog.signOutButton")}
    </Button>
  );
}
